import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type BeforeInstallPromptEvent,
  dismissInstallPrompt,
  getDeferredInstallPrompt,
  getInstallVisits,
  isInstallPromptDismissed,
  onInstallPromptAvailable,
} from '../pwa';

const MIN_VISITS_BEFORE_PROMPT = 2;

/**
 * Tasteful "Install kennel app" banner — pinned to the bottom of the viewport
 * on /account. Only renders when:
 *   - The browser fired beforeinstallprompt (deferred prompt available), AND
 *   - The customer has loaded /account at least MIN_VISITS_BEFORE_PROMPT
 *     times (tracked in localStorage by AccountHome via bumpInstallVisit), AND
 *   - The customer hasn't previously dismissed the banner.
 *
 * Tapping "Install" calls prompt() and clears local state on success; tapping
 * the close icon stores a dismiss flag that persists across visits.
 */
export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(() =>
    getDeferredInstallPrompt(),
  );
  const [hidden, setHidden] = useState<boolean>(() => isInstallPromptDismissed());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return onInstallPromptAvailable((p) => {
      setPrompt(p);
    });
  }, []);

  if (hidden) return null;
  if (!prompt) return null;
  if (getInstallVisits() < MIN_VISITS_BEFORE_PROMPT) return null;

  const handleInstall = async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === 'accepted') {
        setHidden(true);
      } else {
        dismissInstallPrompt();
        setHidden(true);
      }
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    dismissInstallPrompt();
    setHidden(true);
  };

  return (
    <div
      role="region"
      aria-label="Install app"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'max(1rem, env(safe-area-inset-bottom))',
        transform: 'translateX(-50%)',
        width: 'min(420px, calc(100vw - 1.5rem))',
        background: 'var(--surface, white)',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        borderRadius: '14px',
        boxShadow: '0 10px 30px -10px oklch(0 0 0 / 0.18)',
        padding: '0.9rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        zIndex: 50,
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: '10px',
          background: 'var(--brand-primary, var(--primary, #004AAD))',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Download size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.25 }}>
          Install kennel app
        </div>
        <div
          style={{
            fontSize: '0.78rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            marginTop: '0.15rem',
          }}
        >
          Faster access, offline-ready, push updates on your pet's stay.
        </div>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        disabled={busy}
        style={{
          padding: '0.5rem 0.85rem',
          borderRadius: '8px',
          border: 'none',
          background: 'var(--brand-primary, var(--primary, #004AAD))',
          color: 'white',
          fontSize: '0.82rem',
          fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer',
          flexShrink: 0,
        }}
      >
        {busy ? 'Installing…' : 'Install'}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          cursor: 'pointer',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
